import React, { useState } from 'react';
import {
  Box,
  ListItemIcon,
  ListItemText,
  Divider,
  Badge,
  Typography,
  List,
  ListItem,
  ListItemButton,
  SwipeableDrawer,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  IconButton,
  Collapse,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  AccessTime as TimeIcon,
  Assessment as ReportingIcon,
  AccountBalance as FinanceIcon,
  MoreHoriz as MoreIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import useResponsive from '../hooks/useResponsive';
import { menuGroups, itemIcons, getGroupItems } from './navConfig';
import { useTerms } from '../context/TerminologyContext';

// Mobile/tablet navigation: bottom tab bar + "More" swipe drawer.
// Desktop navigation is the persistent Sidebar component; both read the
// shared taxonomy from navConfig.js.

// Drawer width for mobile
const DRAWER_WIDTH = 280;

export default function MenuNavigation({
  tabs,
  currentTab,
  onTabChange,
  pendingApprovalCount = 0,
  pendingPTOCount = 0,
  isProgramAdmin = false,
  isAdmin = false,
  isPayrollAdmin = false,
  isManager = false,
}) {
  const { isMobileOrTablet } = useResponsive();
  const { navLabel } = useTerms();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  const [expandedGroups, setExpandedGroups] = useState({
    time: true,
    reporting: false,
    finance: false,
    settings: false,
  });

  const isEmployee = !isAdmin && !isManager && !isProgramAdmin && !isPayrollAdmin;
  const roleFlags = { isAdmin, isProgramAdmin, isPayrollAdmin, isManager };

  // Build set of tab labels that actually exist (already filtered by permissions in App.js)
  const availableTabLabels = new Set(tabs.map(t => t.label));

  const groupItems = (groupKey) => getGroupItems(groupKey, roleFlags, availableTabLabels);

  // Find tab index by label
  const findTabIndex = (label) => {
    return tabs.findIndex(tab => tab.label === label);
  };

  // Handle menu item click (for mobile drawer)
  const handleMenuItemClick = (label) => {
    const tabIndex = findTabIndex(label);
    if (tabIndex >= 0) {
      onTabChange(null, tabIndex);
    }
    setMobileDrawerOpen(false);
  };

  // Toggle expanded group in mobile drawer
  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Get icon with badge for approvals
  const getItemIcon = (label) => {
    if (label === 'Approvals') {
      return (
        <Badge badgeContent={pendingApprovalCount} color="error" max={99}>
          {itemIcons[label]}
        </Badge>
      );
    }
    if (label === 'Time Off' && pendingPTOCount > 0) {
      return (
        <Badge badgeContent={pendingPTOCount} color="error" max={99}>
          {itemIcons[label]}
        </Badge>
      );
    }
    return itemIcons[label] || <DashboardIcon />;
  };

  // Mobile Drawer Content
  const drawerContent = (
    <Box sx={{ width: DRAWER_WIDTH }}>
      <Box sx={{
        p: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'primary.main',
        color: 'primary.contrastText'
      }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={() => setMobileDrawerOpen(false)} sx={{ color: 'inherit' }}>
          <ChevronLeftIcon />
        </IconButton>
      </Box>
      <Divider />

      <List>
        {/* Dashboard - Direct Link */}
        <ListItem disablePadding>
          <ListItemButton
            selected={currentTab === 0}
            onClick={() => handleMenuItemClick('Dashboard')}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'primary.soft',
                borderLeft: 3,
                borderColor: 'primary.main',
              },
            }}
          >
            <ListItemIcon>{itemIcons['Dashboard']}</ListItemIcon>
            <ListItemText primary="Dashboard" />
          </ListItemButton>
        </ListItem>

        {/* Tenants - Program Admin Only */}
        {isProgramAdmin && (
          <ListItem disablePadding>
            <ListItemButton
              selected={findTabIndex('Tenants') === currentTab}
              onClick={() => handleMenuItemClick('Tenants')}
              sx={{
                '&.Mui-selected': {
                  bgcolor: 'primary.soft',
                  borderLeft: 3,
                  borderColor: 'primary.main',
                },
              }}
            >
              <ListItemIcon>{itemIcons['Tenants']}</ListItemIcon>
              <ListItemText primary="Tenants" />
            </ListItemButton>
          </ListItem>
        )}

        <Divider sx={{ my: 1 }} />

        {/* Menu Groups - only show groups with items */}
        {Object.entries(menuGroups).map(([key, group]) => {
          const items = groupItems(key);
          if (items.length === 0) return null;

          return (
            <React.Fragment key={key}>
              <ListItem disablePadding>
                <ListItemButton onClick={() => toggleGroup(key)}>
                  <ListItemIcon>{group.icon}</ListItemIcon>
                  <ListItemText primary={group.label} />
                  {expandedGroups[key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>
              </ListItem>
              <Collapse in={expandedGroups[key]} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {items.map((item) => {
                    const tabIndex = findTabIndex(item);
                    return (
                      <ListItemButton
                        key={item}
                        selected={currentTab === tabIndex}
                        onClick={() => handleMenuItemClick(item)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            bgcolor: 'primary.soft',
                            borderLeft: 3,
                            borderColor: 'primary.main',
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          {getItemIcon(item)}
                        </ListItemIcon>
                        <ListItemText primary={navLabel(item).replace('My ', '')} />
                      </ListItemButton>
                    );
                  })}
                </List>
              </Collapse>
            </React.Fragment>
          );
        })}
      </List>
    </Box>
  );

  // Build mobile nav items based on role
  const getMobileNavItems = () => {
    const items = [
      { key: 'dashboard', label: 'Home', icon: <DashboardIcon /> },
      {
        key: 'time',
        label: 'Time',
        icon: (pendingApprovalCount + pendingPTOCount) > 0 && !isEmployee ? (
          <Badge badgeContent={pendingApprovalCount + pendingPTOCount} color="error" max={99}>
            <TimeIcon />
          </Badge>
        ) : <TimeIcon />
      },
    ];

    // Only show Reporting for admins/managers
    if (!isEmployee) {
      items.push({ key: 'reporting', label: 'Reporting', icon: <ReportingIcon /> });
    }

    items.push({ key: 'finance', label: 'Finance', icon: <FinanceIcon /> });
    items.push({ key: 'more', label: 'More', icon: <MoreIcon /> });

    return items;
  };

  const mobileNavItems = getMobileNavItems();

  // Get current mobile nav index based on active tab
  const getMobileNavValue = () => {
    const currentLabel = tabs[currentTab]?.label;
    if (currentLabel === 'Dashboard') return 0;

    const timeItems = groupItems('time');
    if (timeItems.includes(currentLabel)) return 1;

    if (!isEmployee) {
      const reportingItems = groupItems('reporting');
      if (reportingItems.includes(currentLabel)) return 2;

      const financeItems = groupItems('finance');
      if (financeItems.includes(currentLabel)) return 3;

      return 4; // More
    } else {
      // Employee: no Reporting menu
      const financeItems = groupItems('finance');
      if (financeItems.includes(currentLabel)) return 2;

      return 3; // More
    }
  };

  const handleMobileNavChange = (event, value) => {
    const item = mobileNavItems[value];
    if (!item) return;

    if (item.key === 'dashboard') {
      onTabChange(null, 0);
    } else if (item.key === 'more') {
      setMobileDrawerOpen(true);
    } else {
      // Navigate directly to first item in the group
      const items = groupItems(item.key);
      if (items.length > 0) {
        const firstItem = items[0];
        const tabIndex = findTabIndex(firstItem);
        if (tabIndex >= 0) {
          onTabChange(null, tabIndex);
        }
      }
    }
  };

  // Desktop uses the Sidebar component — nothing to render here
  if (!isMobileOrTablet) return null;

  // Mobile/Tablet Navigation
  return (
    <>
      {/* Mobile Drawer */}
      <SwipeableDrawer
        anchor="left"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        onOpen={() => setMobileDrawerOpen(true)}
        sx={{
          '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
        }}
      >
        {drawerContent}
      </SwipeableDrawer>

      {/* Bottom Navigation */}
      <Paper
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
        elevation={3}
      >
        <BottomNavigation
          value={getMobileNavValue()}
          onChange={handleMobileNavChange}
          showLabels
          sx={{
            height: 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: 'auto',
              padding: '6px 0',
              '&.Mui-selected': {
                color: 'primary.main',
              },
            },
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.7rem',
              '&.Mui-selected': {
                fontSize: '0.75rem',
              },
            },
          }}
        >
          {mobileNavItems.map((item) => (
            <BottomNavigationAction
              key={item.key}
              label={item.label}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </>
  );
}
